import { useState } from "react";
import { UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  InviteMemberDialog,
  RemoveMemberDialog,
  TransferOwnershipDialog,
} from "./member-dialogs";
import { MemberTableRow } from "./member-table-row";
import { PendingInvitesCard } from "./pending-invites-card";

export function MembersTab() {
  const { auth } = useAuth();
  const utils = trpc.useUtils();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"owner" | "member">("member");

  const [memberToRemove, setMemberToRemove] = useState<{
    userId: string;
    displayName: string | null;
    isSelf: boolean;
  } | null>(null);

  const [memberToTransfer, setMemberToTransfer] = useState<{
    userId: string;
    displayName: string | null;
  } | null>(null);

  const { data: members, isLoading: membersLoading } =
    trpc.tenant.members.useQuery();

  const { data: workspaces } = trpc.tenant.listForUser.useQuery();
  const currentWorkspace = workspaces?.find((w) => w.isCurrent);
  const isOwner = currentWorkspace?.role === "owner";

  const { data: pendingInvites } = trpc.tenant.pendingInvites.useQuery(
    undefined,
    { enabled: isOwner },
  );

  const inviteMutation = trpc.tenant.invite.useMutation({
    onSuccess: (result) => {
      if (result.existing) {
        toast.info("An active invite already exists for this email");
      } else {
        toast.success("Invite created");
      }
      const link = `${window.location.origin}/invite/${result.token}`;
      navigator.clipboard.writeText(link).then(() => {
        toast.info("Invite link copied to clipboard");
      });
      utils.tenant.pendingInvites.invalidate();
      setShowInviteDialog(false);
      setInviteEmail("");
      setInviteRole("member");
    },
    onError: (err) => toastTrpcError(err, "Failed to send invite"),
  });

  const updateRoleMutation = trpc.tenant.updateMemberRole.useMutation({
    onSuccess: () => {
      toast.success("Role updated");
      utils.tenant.members.invalidate();
      utils.tenant.listForUser.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to update member role"),
  });

  const removeMemberMutation = trpc.tenant.removeMember.useMutation({
    onSuccess: () => {
      toast.success(
        memberToRemove?.isSelf ? "You left the workspace" : "Member removed",
      );
      setMemberToRemove(null);
      utils.tenant.members.invalidate();
      utils.tenant.listForUser.invalidate();
      if (memberToRemove?.isSelf) {
        window.location.href = "/projects";
      }
    },
    onError: (err) => toastTrpcError(err, "Failed to remove member"),
  });

  const revokeInviteMutation = trpc.tenant.revokeInvite.useMutation({
    onSuccess: () => {
      toast.success("Invite revoked");
      utils.tenant.pendingInvites.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to revoke invite"),
  });

  const resendInviteMutation = trpc.tenant.resendInvite.useMutation({
    onSuccess: (result) => {
      toast.success(`Invite resent to ${result.email}`);
      const link = `${window.location.origin}/invite/${result.token}`;
      navigator.clipboard.writeText(link).then(() => {
        toast.info("Updated invite link copied to clipboard");
      });
      utils.tenant.pendingInvites.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to resend invite"),
  });

  const transferOwnershipMutation = trpc.tenant.transferOwnership.useMutation({
    onSuccess: () => {
      toast.success("Workspace ownership transferred");
      utils.tenant.members.invalidate();
      utils.tenant.listForUser.invalidate();
      setMemberToTransfer(null);
    },
    onError: (err) => toastTrpcError(err, "Failed to transfer ownership"),
  });

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    inviteMutation.mutate({
      email: inviteEmail.trim(),
      role: inviteRole,
    });
  };

  if (membersLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="space-y-3">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const memberList = members ?? [];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold">
              Team Members
            </CardTitle>
            <CardDescription className="text-xs">
              People who have access to this workspace and its projects.
            </CardDescription>
          </div>
          {isOwner && (
            <Button size="sm" onClick={() => setShowInviteDialog(true)}>
              <UserPlus className="mr-1.5 h-3.5 w-3.5" />
              Invite member
            </Button>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="hidden md:table-cell">Projects</TableHead>
                <TableHead className="hidden md:table-cell">Joined</TableHead>
                <TableHead className="w-[100px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {memberList.map((m) => (
                <MemberTableRow
                  key={m.id}
                  member={m}
                  isSelf={m.userId === auth?.userId}
                  isOwner={isOwner}
                  isUpdatingRole={updateRoleMutation.isPending}
                  onUpdateRole={(userId, role) =>
                    updateRoleMutation.mutate({ userId, role })
                  }
                  onRemove={setMemberToRemove}
                  onTransfer={setMemberToTransfer}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {isOwner && pendingInvites && (
        <PendingInvitesCard
          invites={pendingInvites}
          onResend={(inviteId) => resendInviteMutation.mutate({ inviteId })}
          isResending={resendInviteMutation.isPending}
          onRevoke={(inviteId) => revokeInviteMutation.mutate({ inviteId })}
        />
      )}

      <InviteMemberDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
        inviteEmail={inviteEmail}
        onInviteEmailChange={setInviteEmail}
        inviteRole={inviteRole}
        onInviteRoleChange={setInviteRole}
        onSubmit={handleInvite}
        isPending={inviteMutation.isPending}
      />

      <RemoveMemberDialog
        member={memberToRemove}
        onClose={() => setMemberToRemove(null)}
        onConfirm={(userId) => removeMemberMutation.mutate({ userId })}
        isPending={removeMemberMutation.isPending}
      />

      <TransferOwnershipDialog
        member={memberToTransfer}
        onClose={() => setMemberToTransfer(null)}
        onConfirm={(newOwnerUserId) =>
          transferOwnershipMutation.mutate({ newOwnerUserId })
        }
        isPending={transferOwnershipMutation.isPending}
      />
    </div>
  );
}
